import React, { useState } from "react";
import {
  ContentContainer,
  ModalTitle,
  ButtonsContainer,
} from "./EditTranscriptModal.styled";
import { Button, CircularProgress } from "@mui/material";
import { EditIcon } from "../../Icons";
import { TextInput } from "../../TextInput";
import { useTranslation } from "react-i18next";

interface EditTranscriptModalContentProps {
  transcript: string;
  onSave: (transcript: string) => void | Promise<void>;
  onClose: () => void;
}

const EditTranscriptModalContent: React.FC<EditTranscriptModalContentProps> = ({
  transcript,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState(transcript);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(editedTranscript);
      setIsEditing(false);
      onClose();
    } catch (error) {
      console.error("Error saving transcript:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedTranscript(transcript);
    setIsEditing(false);
  };

  return (
    <ContentContainer>
      <ModalTitle>{t("editTranscriptModal.title")}</ModalTitle>

      <TextInput
        multiline
        rows={12}
        value={editedTranscript}
        onChange={(
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => setEditedTranscript(e.target.value)}
        disabled={!isEditing || isSaving}
        fullWidth
      />

      <ButtonsContainer>
        {!isEditing ? (
          <>
            <Button variant="outlined" onClick={handleEdit}>
              <EditIcon width="16px" height="16px" />
              {t("editTranscriptModal.editButton")}
            </Button>
            <Button variant="contained" onClick={onClose}>
              {t("editTranscriptModal.closeButton")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t("editTranscriptModal.closeButton")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("editTranscriptModal.saveButton")
              )}
            </Button>
          </>
        )}
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default EditTranscriptModalContent;
